import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, Gift, Loader2, Lock, Tag } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { formatApiError } from "@/context/AuthContext";
import { BUNDLE } from "@/lib/bundle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE_SHIPPING = 50;
const SHIPPING = 4.95;
const formatPrice = (p) => `€${p.toFixed(2).replace(".", ",")}`;

const inputClass =
  "w-full bg-slate-900/70 border border-slate-700 focus:border-[#00FF66] focus:ring-1 focus:ring-[#00FF66]/40 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">{label}</span>
    <input data-testid={testid} className={inputClass} {...props} />
  </label>
);

export default function Checkout() {
  const { items, subtotal, bundle } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    postal_code: "",
    city: "",
    country: "NL",
  });
  const [submitting, setSubmitting] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  const afterBundle = subtotal - bundle.discount;
  const discount = promo?.percent_off
    ? (afterBundle * promo.percent_off) / 100
    : promo?.amount_off
      ? Math.min(afterBundle, promo.amount_off / 100)
      : 0;
  const shipping = subtotal >= FREE_SHIPPING || subtotal === 0 ? 0 : SHIPPING;
  const total = afterBundle - discount + shipping;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoBusy(true);
    setPromoError("");
    try {
      const res = await axios.post(`${API}/payments/validate-promo`, { code: promoInput.trim() });
      setPromo(res.data);
      toast.success(`Code ${res.data.code} toegepast: ${res.data.percent_off}% korting`);
    } catch (err) {
      setPromo(null);
      setPromoError(formatApiError(err.response?.data?.detail, "Ongeldige kortingscode"));
    } finally {
      setPromoBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Je winkelmand is leeg");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        customer: form,
        origin_url: window.location.origin,
        promo_code: promo ? promo.code : null,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error(err);
      toast.error(formatApiError(err.response?.data?.detail, "Er ging iets mis bij het starten van de betaling. Probeer opnieuw."));
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="bg-[#090D16] text-slate-100 min-h-screen">
      <div className="noise-overlay" />
      <header className="border-b border-slate-800 bg-[#090D16]/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" data-testid="checkout-logo-link"><Logo compact /></a>
          <span className="flex items-center gap-2 text-xs text-slate-400 font-mono-brand uppercase tracking-widest">
            <Lock className="w-3.5 h-3.5 text-[#00FF66]" /> Veilig afrekenen
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          data-testid="checkout-back-button"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-[#00FF66] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Terug naar de shop
        </button>

        {items.length === 0 ? (
          <div className="text-center py-24" data-testid="checkout-empty-state">
            <h1 className="font-display text-6xl text-white uppercase tracking-wide">Je mand is leeg</h1>
            <p className="mt-4 text-slate-400">Voeg eerst een blaster toe voordat je afrekent.</p>
            <button
              data-testid="checkout-empty-shop-button"
              onClick={() => navigate("/#blasters")}
              className="mt-8 bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase px-8 py-4 rounded-full glow-lime"
            >
              Naar de blasters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-3 space-y-8"
              data-testid="checkout-form"
            >
              <div>
                <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-wide text-white leading-none">
                  Afrekenen
                </h1>
                <p className="mt-3 text-sm text-slate-400">
                  Vul je gegevens in — daarna ga je naar onze beveiligde betaalomgeving (Stripe) voor iDEAL, Bancontact of creditcard.
                </p>
              </div>

              <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-5">
                <h2 className="font-display text-2xl uppercase tracking-wide text-white">
                  <span className="text-[#00FF66] mr-2">01</span>Contactgegevens
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Volledige naam" testid="checkout-name-input" required minLength={2}
                    placeholder="Jan de Vries" value={form.name} onChange={set("name")} />
                  <Field label="E-mailadres" testid="checkout-email-input" required type="email"
                    placeholder="jan@voorbeeld.nl" value={form.email} onChange={set("email")} />
                </div>
              </section>

              <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-5">
                <h2 className="font-display text-2xl uppercase tracking-wide text-white">
                  <span className="text-[#00E5FF] mr-2">02</span>Bezorgadres
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field label="Straat + huisnummer" testid="checkout-address-input" required minLength={3}
                      placeholder="Splatlaan 12" value={form.address} onChange={set("address")} />
                  </div>
                  <Field label="Postcode" testid="checkout-postal-input" required minLength={4}
                    placeholder="1234 AB" value={form.postal_code} onChange={set("postal_code")} />
                  <Field label="Plaats" testid="checkout-city-input" required minLength={2}
                    placeholder="Amsterdam" value={form.city} onChange={set("city")} />
                  <label className="block sm:col-span-2">
                    <span className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">Land</span>
                    <select
                      data-testid="checkout-country-select"
                      className={inputClass}
                      value={form.country}
                      onChange={set("country")}
                    >
                      <option value="NL">Nederland</option>
                      <option value="BE">België</option>
                    </select>
                  </label>
                </div>
              </section>

              <section className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8">
                <h2 className="font-display text-2xl uppercase tracking-wide text-white mb-5">
                  <span className="text-[#FF087B] mr-2">03</span>Betaalmethode
                </h2>
                <div className="flex flex-wrap gap-3" data-testid="checkout-payment-methods">
                  {["iDEAL", "Bancontact", "Creditcard"].map((m) => (
                    <span
                      key={m}
                      className="flex items-center gap-2 bg-slate-800/70 border border-slate-700 px-4 py-2.5 rounded-full text-sm font-bold text-slate-200"
                    >
                      <CreditCard className="w-4 h-4 text-[#00FF66]" /> {m}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  Je kiest je bank of kaart in de volgende stap op de beveiligde Stripe-betaalpagina.
                </p>
              </section>

              <button
                type="submit"
                data-testid="checkout-submit-button"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase tracking-wider py-5 rounded-full glow-lime hover:glow-lime-strong hover:scale-[1.01] active:scale-95 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Betaling voorbereiden...
                  </>
                ) : (
                  <>Plaats bestelling — {formatPrice(total)}</>
                )}
              </button>
            </motion.form>

            <motion.aside
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="lg:col-span-2"
            >
              <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-6 sm:p-8 lg:sticky lg:top-8" data-testid="checkout-summary">
                <h2 className="font-display text-2xl uppercase tracking-wide text-white mb-6">Jouw bestelling</h2>
                <ul className="space-y-4">
                  {items.map((item) => (
                    <li key={item.id} className="flex items-center gap-4" data-testid={`summary-item-${item.id}`}>
                      <div className="relative shrink-0">
                        <img src={item.image} alt={item.name} className="w-14 h-14 rounded-xl object-cover" />
                        <span className="absolute -top-2 -right-2 bg-[#FF087B] text-white text-[10px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center">
                          {item.qty}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">{item.name}</p>
                        <p className="text-xs text-slate-500">{formatPrice(item.price)} per stuk</p>
                      </div>
                      <p className="text-sm font-bold text-white">{formatPrice(item.price * item.qty)}</p>
                    </li>
                  ))}
                  {bundle.gift && (
                    <li className="flex items-center gap-4" data-testid="summary-item-bundel-pack">
                      <div className="w-14 h-14 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center shrink-0">
                        <Gift className="w-6 h-6 text-[#00E5FF]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-white truncate">{BUNDLE.giftName}</p>
                        <p className="text-xs text-slate-500 truncate">{BUNDLE.giftDescription}</p>
                      </div>
                      <p className="text-sm font-bold text-[#00E5FF]">Gratis</p>
                    </li>
                  )}
                </ul>

                {bundle.nudge && (
                  <p data-testid="checkout-bundle-nudge" className="mt-4 text-xs text-slate-400 flex items-center gap-2">
                    <Gift className="w-3.5 h-3.5 text-[#FF087B]" /> {bundle.nudge}
                  </p>
                )}

                <div className="mt-6 pt-5 border-t border-slate-800">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="w-4 h-4 text-slate-500 absolute left-3.5 top-1/2 -translate-y-1/2" />
                      <input
                        data-testid="promo-input"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder="Kortingscode"
                        className="w-full bg-slate-900/70 border border-slate-700 focus:border-[#00FF66] rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder:text-slate-500 uppercase outline-none transition-colors"
                      />
                    </div>
                    <button
                      type="button"
                      data-testid="promo-apply-button"
                      onClick={applyPromo}
                      disabled={promoBusy}
                      className="bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-[#00FF66]/50 text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60 flex items-center gap-2"
                    >
                      {promoBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                      Toepassen
                    </button>
                  </div>
                  {promo && (
                    <p data-testid="promo-applied" className="text-xs text-[#00FF66] font-bold mt-2">
                      Code {promo.code} actief — {promo.percent_off}% korting
                    </p>
                  )}
                  {promoError && (
                    <p data-testid="promo-error" className="text-xs text-[#FF087B] font-semibold mt-2">{promoError}</p>
                  )}
                </div>

                <div className="mt-5 pt-5 border-t border-slate-800 space-y-2.5 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Subtotaal</span>
                    <span data-testid="summary-subtotal">{formatPrice(subtotal)}</span>
                  </div>
                  {bundle.discount > 0 && (
                    <div className="flex justify-between text-[#00FF66] font-bold">
                      <span>Bundelkorting ({bundle.qty} blasters × €{BUNDLE.discountPerBlaster})</span>
                      <span data-testid="summary-bundle-discount">-{formatPrice(bundle.discount)}</span>
                    </div>
                  )}
                  {discount > 0 && (
                    <div className="flex justify-between text-[#00FF66] font-bold">
                      <span>Korting ({promo.code})</span>
                      <span data-testid="summary-discount">-{formatPrice(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-slate-400">
                    <span>Verzending (express)</span>
                    <span data-testid="summary-shipping" className={shipping === 0 ? "text-[#00FF66] font-bold" : ""}>
                      {shipping === 0 ? "Gratis" : formatPrice(shipping)}
                    </span>
                  </div>
                  <div className="flex justify-between text-white font-extrabold text-lg pt-2.5 border-t border-slate-800">
                    <span>Totaal</span>
                    <span data-testid="summary-total">{formatPrice(total)}</span>
                  </div>
                  <p className="text-xs text-slate-500 pt-1">Inclusief btw. 10.000 gel orbs gratis bij elke blaster.</p>
                </div>
              </div>
            </motion.aside>
          </div>
        )}
      </main>
    </div>
  );
}
